import type { CheckEligibilityRuleError } from '@ben/columbia/api';
import { useMutationCheckEligibilityRuleQuery } from '@ben/columbia/api';
import Button from '@ben/orion/deprecated-button';
import { IconName } from '@ben/orion/icon';
import InlineTextButton from '@ben/orion/inline-text-button';
import Input, { InputVariant } from '@ben/orion/input';
import Tooltip from '@ben/orion/tooltip';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type { IModularBenefitDetail } from '@ben/shared/types/columbia';
import { exportCsvFile } from '@ben/shared/utils/export-csv-file';
import { isNumber } from 'lodash';
import { memo, useCallback, useMemo, useState } from 'react';
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import styled, { css } from 'styled-components';
import AIToolWrapper from '../../assistant/ai-tool-wrapper';
import { fieldCannotBeBlankErrorMessage } from '../../shared/constants';
import useSetDirtyValue from '../hooks/use-set-dirty-value';
import ReadOnlyBanner from '../read-only-banner';

interface EligibilityRulesProps {
  isScheduledVersion?: boolean;
}

const EligibilityRules = ({ isScheduledVersion }: EligibilityRulesProps) => {
  const { mutate: checkEligibilityRule, isLoading: isLoadingCheck } =
    useMutationCheckEligibilityRuleQuery();

  const { control, setValue, getValues, setError, trigger } =
    useFormContext<IModularBenefitDetail>();
  const setDirtyValue = useSetDirtyValue<IModularBenefitDetail>();
  const { fields, append, remove } = useFieldArray({
    name: 'eligibilityRules',
    control,
  });

  const [queryBeingTested, setQueryBeingTested] = useState<number>();

  const employerId = useWatch({
    control,
    name: 'employer',
    exact: true,
  });
  const hasEmployer = isNumber(employerId);

  const initialRule = useMemo(
    () => ({
      query: '',
      prompt: '',
      needsTesting: true,
    }),
    []
  );

  const checkEligibilityRuleQuery = (index: number) => {
    const query = getValues(`eligibilityRules.${index}.query`);
    setQueryBeingTested(index);
    checkEligibilityRule(
      { employerId: Number(employerId), query },
      {
        onSuccess: (response: string) => {
          setNeedsTestingValue(index, false);
          exportCsvFile({ data: response, fileName: 'query-result' });
        },
        onError: (error: CheckEligibilityRuleError) => {
          setError(`eligibilityRules.${index}.query`, {
            type: 'manual',
            message: error?.response?.data?.detail,
          });
        },
        onSettled: () => {
          setQueryBeingTested(undefined);
        },
      }
    );
  };

  const setNeedsTestingValue = (index: number, value: boolean) => {
    setValue(`eligibilityRules.${index}.needsTesting`, value);
    void trigger(`eligibilityRules.${index}.query`);
  };

  const handleAddEligibilityRule = useCallback(() => {
    if (fields.length === 0) setValue('eligibilityRules', []);

    append(initialRule);
  }, [append, fields.length, initialRule, setValue]);

  return (
    <Container>
      <TitleContainer>
        <Typography variant={TypographyVariant.h2}>
          Benefit eligibility rules
        </Typography>
        <Tooltip
          tooltipText="You need to assign an employer to this benefit to add an eligibility rule"
          noAnchorRole
          hideTooltip={hasEmployer}
        >
          <Button
            label="Add eligibility rule"
            onClick={handleAddEligibilityRule}
            disabled={!hasEmployer || isScheduledVersion}
          />
        </Tooltip>
      </TitleContainer>
      {isScheduledVersion && <ReadOnlyBanner />}
      {fields?.length === 0 ? (
        <Typography variant={TypographyVariant.bodyLg1}>
          No rules added yet.
        </Typography>
      ) : (
        fields.map((rule, index) => {
          return (
            <ContentWrapper key={rule.id}>
              <TitleContainer>
                <Typography variant={TypographyVariant.h3}>
                  Query #{index + 1}
                </Typography>
                <InlineTextButton
                  label="Remove"
                  onClick={() => {
                    remove(index);
                    setDirtyValue(
                      'eligibilityRules',
                      getValues('eligibilityRules')
                    );
                  }}
                  disabled={isScheduledVersion}
                />
              </TitleContainer>
              <AIToolWrapper
                toolName="eligibilityRuleQuery"
                fieldName={`eligibilityRules.${index}.query`}
              >
                <Controller
                  name={`eligibilityRules.${index}.query`}
                  control={control}
                  rules={{
                    validate: {
                      required: (value) => {
                        if (!value) return fieldCannotBeBlankErrorMessage;
                        if (getValues(`eligibilityRules.${index}.needsTesting`))
                          return 'This query has not been tested yet. Test query to proceed.';
                        return;
                      },
                    },
                  }}
                  render={({ field, fieldState }) => {
                    const { value, onChange, ...rest } = field;
                    return (
                      <Input
                        key={rule.id}
                        {...rest}
                        name="query"
                        showLabel={false}
                        value={value ?? ''}
                        width="100%"
                        variant={InputVariant.TextArea}
                        hasError={!!fieldState.error}
                        errorMessage={fieldState.error?.message}
                        data-testid={`query-${index}`}
                        onChange={(event) => {
                          onChange(event);
                          setNeedsTestingValue(index, true);
                        }}
                        disabled={isScheduledVersion}
                      />
                    );
                  }}
                />
              </AIToolWrapper>
              <Button
                label={
                  getValues(`eligibilityRules.${index}.needsTesting`)
                    ? 'Test Query'
                    : 'Re-test Query'
                }
                isLoading={isLoadingCheck && queryBeingTested === index}
                onClick={() => {
                  if (getValues(`eligibilityRules.${index}.query`).trim()) {
                    void checkEligibilityRuleQuery(index);
                  } else {
                    setValue(`eligibilityRules.${index}.query`, '');
                    void trigger(`eligibilityRules.${index}.query`);
                  }
                }}
                iconName={IconName.Download}
                width="164px"
                disabled={isScheduledVersion}
              />
            </ContentWrapper>
          );
        })
      )}
    </Container>
  );
};

export default memo(EligibilityRules);

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    width: 100%;
    border: 1px solid ${theme.palette.neutral[60]};
    border-radius: 10px;
    padding: ${theme.spacing(3)};
  `}
`;

const TitleContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  `}
`;

const ContentWrapper = styled(Container)`
  ${({ theme }) => css`
    background-color: ${theme.palette.primary.bg};
    border: none;
  `}
`;
